import type { PlanBillingInterval, UpgradePlanName } from './plan-pricing';
import { PLAN_LIST_PRICING_USD } from './plan-pricing';

// Plans as the billing page and the plans modal present them. Prices are USD list prices;
// other currencies come from Dodo at runtime.
export type PlanPrice = {
  compareAt: number | null;
  current: number;
  annualTotal?: number;
  currency?: string;
};

export type PlanFeatureItem = string | { text: string; tooltip: string };

export interface PlanFeatureGroup {
  label: string;
  items: PlanFeatureItem[];
}

// Shown on the Monthly/Annually switch on the billing page and in the plans modal.
export const ANNUAL_BILLING_SAVINGS_PERCENT = 15;

export interface PlanDefinition {
  name: UpgradePlanName;
  description: string;
  pricing: Record<PlanBillingInterval, PlanPrice>;
  billingNote?: string;
  defaultFeatures?: string[];
  featureGroups: PlanFeatureGroup[];
  // Short list for the plans modal; the billing page shows the full feature groups.
  keyFeatures: string[];
  featured?: boolean;
  recommended?: boolean;
}

export const PLAN_DEFINITIONS: PlanDefinition[] = [
  {
    name: 'Starter',
    description: 'For solo professionals and freelancers',
    pricing: PLAN_LIST_PRICING_USD.Starter,
    defaultFeatures: ['1 Seat included', '2,000 AI credits/mo', '10 Content sources'],
    keyFeatures: ['Document allowance', 'Template access', 'Sharing options', 'Support level'],
    featureGroups: [
      {
        label: 'Included features:',
        items: ['All document types', 'Limited integrations:'],
      },
      {
        label: 'Document output:',
        items: ['Export to PDF', 'E-signatures'],
      },
    ],
    featured: false,
    recommended: false,
  },
  {
    name: 'Professional',
    description: 'For small teams (2–5 people)',
    pricing: PLAN_LIST_PRICING_USD.Professional,
    defaultFeatures: [
      '3 Seats total · Additional seats available',
      '6,000 AI credits/workspace',
      '30 Content sources',
    ],
    keyFeatures: ['Document allowance', 'Branding controls', 'Export formats', 'Workspace options'],
    featureGroups: [
      {
        label: 'Everything in Starter, plus:',
        items: [
          {
            text: 'Remove proposal.biz branding',
            tooltip:
              'By default, public documents display a "Made with proposal.biz" badge. Upgrade to the Professional plan to remove this branding.',
          },
          'Custom domain',
          'All integrations:',
          'Chat and email support (24h response)',
        ],
      },
      {
        label: 'Document output:',
        items: ['Export to PDF & PPT', 'Send & track opens'],
      },
    ],
    featured: true,
    recommended: true,
  },
  {
    name: 'Business',
    description: 'For growing teams (5–20 people)',
    pricing: PLAN_LIST_PRICING_USD.Business,
    defaultFeatures: [
      '10 Seats total · Additional seats available',
      '10,000 AI credits/workspace',
      '100 Content sources',
    ],
    keyFeatures: ['Team allowance', 'Email options', 'Administration', 'Support level'],
    featureGroups: [
      {
        label: 'Everything in Professional, plus:',
        items: ['Advanced Analytics', 'Priority support — 4h response', 'Bring your own API key'],
      },
    ],
    featured: false,
    recommended: false,
  },
];

export type PlanPricingOverrides = Partial<
  Record<UpgradePlanName, Partial<Record<PlanBillingInterval, PlanPrice | null>>>
>;

const inUsd = (price: PlanPrice): PlanPrice => ({ ...price, currency: 'USD' });

// Plans priced in another currency. A price Dodo couldn't give keeps the USD list price.
export function localizePlans(overrides?: PlanPricingOverrides | null): PlanDefinition[] {
  return PLAN_DEFINITIONS.map((plan) => ({
    ...plan,
    pricing: {
      monthly: overrides?.[plan.name]?.monthly ?? inUsd(plan.pricing.monthly),
      annual: overrides?.[plan.name]?.annual ?? inUsd(plan.pricing.annual),
    },
  }));
}
